import React, { useMemo, useState } from 'react';

import { HasDateRange, RuleTimeline } from '@ndvr/billing-api';

import { Centered, ConfirmModal, ModalText, PageSpinner, Text } from 'widgets';

import { TimelineCard, TimelineStatus } from './TimelineCard';
import { DashedSeparator } from './widgets/DashedSeparator';

interface RuleProp<T extends HasDateRange> {
    rule: T;
}

interface Props<T extends HasDateRange> {
    timeline?: RuleTimeline<T>;
    timelineForOverrides?: boolean;
    isLoading?: boolean;
    onEdit: (rule: T) => void;
    onDelete: (rule: T) => Promise<unknown>;
    firstColumnRenderer: (props: RuleProp<T>) => JSX.Element;
    secondColumnRenderer: (props: RuleProp<T>) => JSX.Element;
}

export const TimelinePanel = <T extends HasDateRange>({
    timeline,
    timelineForOverrides,
    isLoading,
    onEdit,
    onDelete,
    firstColumnRenderer,
    secondColumnRenderer,
}: Props<T>) => {
    const [showExpired, setShowExpired] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState<T>();

    const expiredRulesReversed = useMemo(() => timeline?.expiredRules.reverse() ?? [], [timeline?.expiredRules]);
    const hiddenExpiredRuleCount = Math.max(0, expiredRulesReversed.length - (timelineForOverrides ? 0 : 1));

    if (isLoading) {
        return <PageSpinner />;
    }
    if (!timeline || isEmpty(timeline)) {
        return (
            <Centered>
                <Text $italic $themeColor="subtle">
                    Empty
                </Text>
            </Centered>
        );
    }
    return (
        <>
            {timeline.activeRules.map((rule, index) => (
                <TimelineCard
                    key={index}
                    firstColumn={firstColumnRenderer({ rule })}
                    secondColumn={secondColumnRenderer({ rule })}
                    status={TimelineStatus.ACTIVE}
                    startDate={rule.startDate}
                    endDate={rule.endDate}
                    onEdit={timelineForOverrides ? () => onEdit(rule) : undefined}
                />
            ))}
            {timeline.upcomingRules.map((rule, index) => (
                <TimelineCard
                    key={index}
                    firstColumn={firstColumnRenderer({ rule })}
                    secondColumn={secondColumnRenderer({ rule })}
                    status={TimelineStatus.UPCOMING}
                    startDate={rule.startDate}
                    endDate={rule.endDate}
                    onEdit={() => onEdit(rule)}
                    onDelete={() => setRuleToDelete(rule)}
                />
            ))}
            {expiredRulesReversed.length > 0 && (!timelineForOverrides || showExpired) && (
                <DashedSeparator label={TimelineStatus.EXPIRED} />
            )}
            {!showExpired && expiredRulesReversed.length > 0 && !timelineForOverrides && (
                <TimelineCard
                    firstColumn={firstColumnRenderer({ rule: expiredRulesReversed[0] })}
                    secondColumn={secondColumnRenderer({ rule: expiredRulesReversed[0] })}
                    status={TimelineStatus.EXPIRED}
                    startDate={expiredRulesReversed[0].startDate}
                    endDate={expiredRulesReversed[0].endDate}
                />
            )}
            {!showExpired && hiddenExpiredRuleCount > 0 && (
                <Centered>
                    <Text onClick={() => setShowExpired(true)}>
                        Show {!timelineForOverrides ? 'more' : ''} expired ({hiddenExpiredRuleCount})
                    </Text>
                </Centered>
            )}
            {showExpired &&
                expiredRulesReversed.map((rule, index) => (
                    <TimelineCard
                        key={index}
                        firstColumn={firstColumnRenderer({ rule })}
                        secondColumn={secondColumnRenderer({ rule })}
                        status={TimelineStatus.EXPIRED}
                        startDate={rule.startDate}
                        endDate={rule.endDate}
                    />
                ))}
            {ruleToDelete && (
                <ConfirmModal
                    modalTitle={'Delete ' + (timelineForOverrides ? 'Override' : 'Profile')}
                    onConfirm={() => onDelete(ruleToDelete)}
                    buttonProps={{ buttonType: 'destructive', children: 'Delete' }}
                    onClose={() => setRuleToDelete(undefined)}
                >
                    <ModalText>
                        Are you sure you want to delete this {timelineForOverrides ? 'override' : 'profile'}?
                    </ModalText>
                    <ModalText>This action cannot be undone.</ModalText>
                </ConfirmModal>
            )}
        </>
    );
};

const isEmpty = (timeline: RuleTimeline<any>): boolean => {
    return (
        timeline.activeRules.length === 0 && timeline.expiredRules.length === 0 && timeline.upcomingRules.length === 0
    );
};
